import React, { useState, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { departmentData, getDepartmentRisk, RISK_LEVELS, getDetailedInsights } from '../data/departmentRiskLogic';

export const DepartmentRiskAnalysis = () => {
  const [selectedId, setSelectedId] = useState(departmentData[0].id);
  const selectedDept = useMemo(() => departmentData.find(d => d.id === selectedId), [selectedId]);
  const risk = useMemo(() => getDepartmentRisk(selectedDept.metrics), [selectedDept]);
  const insights = useMemo(() => getDetailedInsights(selectedDept), [selectedDept]);

  const radarOption = {
    radar: {
      indicator: [
        { name: 'WE Score', max: 100 },
        { name: 'Stress (Inv)', max: 100 },
        { name: 'Trygghet', max: 100 },
        { name: 'Konflikt (Inv)', max: 100 }
      ],
      shape: 'circle',
      name: { textStyle: { color: '#64748b', fontSize: 10, fontWeight: 'bold' } }
    },
    series: [{
      type: 'radar',
      data: [{
        value: [
          selectedDept.metrics.we, 
          100 - selectedDept.metrics.stress, 
          selectedDept.metrics.safety, 
          100 - selectedDept.metrics.conflict
        ],
        name: selectedDept.name,
        itemStyle: { color: '#6366f1' },
        areaStyle: { opacity: 0.2 }
      }]
    }]
  };

  const trendOption = {
    grid: { top: 10, right: 10, bottom: 20, left: 30 },
    xAxis: { type: 'category', show: false },
    yAxis: { type: 'value', max: 100, show: false },
    series: [{
      data: selectedDept.trends,
      type: 'line',
      smooth: true,
      symbol: 'none',
      lineStyle: { width: 3, color: '#6366f1' },
      areaStyle: {
        color: {
          type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [{ offset: 0, color: 'rgba(99, 102, 241, 0.2)' }, { offset: 1, color: 'transparent' }]
        }
      }
    }]
  };

  return (
    <div className="space-y-8 pb-20">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Department Risk Analysis</h1>
          <p className="text-slate-500 mt-1 italic">Dypdykk i arbeidsmiljørisiko per avdeling.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" icon={<SafeIcon name="Download" />}>Eksporter Avdelingsrapport</Button>
          <Button variant="primary" icon={<SafeIcon name="Plus" />}>Ny Kartlegging</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Department List Sidebar */}
        <div className="space-y-4">
          <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-2">Avdelinger</h3>
          {departmentData.map((dept) => {
            const deptRisk = getDepartmentRisk(dept.metrics);
            return (
              <motion.div
                key={dept.id}
                whileHover={{ x: 4 }}
                onClick={() => setSelectedId(dept.id)}
                className={`cursor-pointer p-4 rounded-3xl border-2 transition-all group ${
                  selectedId === dept.id 
                    ? 'bg-slate-900 border-slate-900 text-white shadow-xl' 
                    : 'bg-white border-slate-100 hover:border-slate-200'
                }`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-bold text-sm uppercase tracking-tight">{dept.name}</h4>
                    <p className={`text-[10px] font-bold ${selectedId === dept.id ? 'text-slate-400' : 'text-slate-400'}`}>
                      {dept.head}
                    </p>
                  </div>
                  <div className={`w-3 h-3 rounded-full bg-${deptRisk.color}-500 shadow-[0_0_10px_rgba(0,0,0,0.1)]`} />
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Main Analysis View */}
        <div className="lg:col-span-3 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Risk Profile Card */}
            <Card className="border-none shadow-xl overflow-hidden relative group">
              <div className={`absolute top-0 right-0 w-32 h-32 bg-${risk.color}-500/5 rounded-full -mr-16 -mt-16 transition-all group-hover:scale-150`} />
              <CardHeader 
                title="Risk Profile" 
                subtitle={selectedDept.name} 
                action={<span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase ${risk.bg} ${risk.text} border ${risk.border}`}>{risk.label}</span>} 
              />
              <CardContent className="space-y-8">
                <div className="flex justify-between items-end">
                  <div>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Health Index</p>
                    <div className={`text-5xl font-black text-${risk.color}-600 tracking-tighter`}>{selectedDept.metrics.we}%</div>
                  </div>
                  <div className="w-32 h-16">
                    <ReactECharts option={trendOption} style={{ height: '100%' }} />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  {[
                    { label: 'STRESS', val: selectedDept.metrics.stress, color: 'rose' },
                    { label: 'TRYGGHET', val: selectedDept.metrics.safety, color: 'emerald' },
                    { label: 'KONFLIKT', val: selectedDept.metrics.conflict, color: 'orange' },
                  ].map(m => (
                    <div key={m.label} className="text-center p-3 bg-slate-50 rounded-2xl">
                      <p className="text-[9px] font-black text-slate-400 mb-1">{m.label}</p>
                      <p className={`text-sm font-black text-${m.color}-600`}>{m.val}%</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Radar Comparison */}
            <Card>
              <CardHeader title="Dimensjonsanalyse" subtitle="Balanse mellom nøkkelindikatorer" icon={<SafeIcon name="Activity" className="text-indigo-600" />} />
              <CardContent>
                <div className="h-64">
                  <ReactECharts option={radarOption} style={{ height: '100%' }} />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Report Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Card className="border-l-4 border-l-slate-900">
                <CardHeader title="Risikoindikatorer" icon={<SafeIcon name="AlertTriangle" className="text-rose-600" />} />
                <CardContent className="space-y-4">
                  {selectedDept.topRisks.map((riskItem, i) => (
                    <div key={i} className="flex gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-100 items-start hover:bg-white hover:shadow-md transition-all">
                      <div className="w-8 h-8 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center shrink-0">
                        <SafeIcon name="Info" size={16} />
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-slate-900">{riskItem}</h4>
                        <p className="text-[10px] text-slate-500 mt-1 leading-relaxed">
                          Identifisert via mønstergjenkjenning i nylig kartlegging. Krever oppfølging i ledergruppen.
                        </p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="bg-indigo-600 border-none text-white">
                <CardHeader title="Anbefalt Tiltak" icon={<SafeIcon name="Zap" className="text-indigo-200" />} />
                <CardContent className="space-y-4">
                  <div className="p-4 bg-white/10 rounded-2xl border border-white/10">
                    <span className="text-[9px] font-black uppercase tracking-widest text-indigo-200">Prioritet: {insights.priority}</span>
                    <h4 className="font-bold text-sm mt-1">{insights.status}</h4>
                    <p className="text-xs text-indigo-100 mt-2 leading-relaxed">
                      {insights.action}
                    </p>
                  </div>
                  <Button variant="secondary" className="w-full bg-white text-indigo-600 border-none">Start Tiltaksprosess</Button>
                </CardContent>
              </Card>

              <Card className="bg-slate-50 border-slate-200">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center text-slate-400">
                    <SafeIcon name="ShieldCheck" className="text-2xl" />
                  </div>
                  <div>
                    <h4 className="text-xs font-black text-slate-900 uppercase">Compliance</h4>
                    <p className="text-[10px] text-slate-500 font-medium">Oppfyller § 4-3 krav for denne avdelingen.</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};